import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  get, sortBy, find,
} from 'lodash';
import { Paper, Tooltip, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { connect } from 'react-redux';
import { nanoid } from 'nanoid';
import MUIDataTable from 'mui-datatables';
import { bindActionCreators } from 'redux';

import LocationEditForm from './location-edit-form';
import BulkUploadModal from './bulk-upload-modal';
import BulkEditLocations from './bulk-edit-locations';
import CustomToolbar from './custom-toolbar';
import withPersistentSettings from '../common/table-with-persistent-settings';

import * as snackbarActions from '../../actions/snackbar';
import zoneActions from '../../actions/zones';
import locationActions from '../../actions/locations';
import marketActions from '../../actions/markets';
import vehicleActions from '../../actions/vehicles';
import {
  LOC_OPEN as OPEN, shouldFetchList,
  LOC_DELETED as DELETED,
} from '../../lib/utils';
import WrappedSpinner from '../common/wrapped-spinner';
import WarnBeforeDeleting from '../common/warn-before-deleting';

const NAME = 'Name';
const TYPE = 'Type';
const STATUS = 'Status';
const ACTUAL_VEHICLES = 'Actual Vehicles';
const TARGET = 'Target';
const CAPACITY = 'Capacity';
const ACTIONS = 'Actions';
const DELETE_LOC = 'Delete Location';
const DELETE_WARN = 'Are you sure you want to delete this Location?';
const DELETE_CONFIRM = 'Successfully deleted Location';
const DELETE_ERROR = 'Error deleting Location';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
};

function matchingLocation(locations, locId) {
  return find(locations, (location) => location._id === locId);
}

class LocationTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLocationFormOpen: false,
      editLocation: { },
      editing: false,
      isBulkUploadFormOpen: false,
      locationIds: [],
      isBulkEditFormOpen: false,
      warnBeforeDeleting: false,
      bulkDeleting: false,
    };

    this.handleViewLocation = this.handleViewLocation.bind(this);
    this.handleCloseForm = this.handleCloseForm.bind(this);
    this.handleSaveForm = this.handleSaveForm.bind(this);
    this.handleViewLocation = this.handleViewLocation.bind(this);
    this.handleAddLocation = this.handleAddLocation.bind(this);
    this.handleUploadLocations = this.handleUploadLocations.bind(this);
    this.handleCloseBulkUploadForm = this.handleCloseBulkUploadForm.bind(this);
    this.handleSaveBulkUploadForm = this.handleSaveBulkUploadForm.bind(this);
    this.handleOpenBulkEdit = this.handleOpenBulkEdit.bind(this);
    this.handleCloseBulkEdit = this.handleCloseBulkEdit.bind(this);
    this.handleSaveBulkEdit = this.handleSaveBulkEdit.bind(this);
    this.handleDeleteLocation = this.handleDeleteLocation.bind(this);
    this.handleBulkDeleteLocations = this.handleBulkDeleteLocations.bind(this);
  }

  componentDidMount() {
    const {
      locations, marketVehicles, fetchVehicles, fetchLocations, match,
    } = this.props;
    const marketId = get(match, 'params.marketId');

    if (marketId) {
      if (shouldFetchList(marketId, marketVehicles)) fetchVehicles({ market_id: marketId });
      if (shouldFetchList(marketId, locations)) fetchLocations({ market_id: marketId });
    }
  }

  get locations() {
    const locations = get(this, 'props.locations', []);
    return sortBy(
      locations,
      (location) => location.name.toLowerCase(),
    );
  }

  get tableOptions() {
    const { tableOptions } = this.props;
    const basicOptions = tableOptions;
    basicOptions.selectableRows = 'multiple';

    basicOptions.onRowClick = (rowData) => {
      this.handleViewLocation(rowData[0]);
    };

    basicOptions.customToolbarSelect = (selectedRowObjects) => {
      const { data } = selectedRowObjects;

      const selectedRows = data.map((item) => item.dataIndex);
      const { locations = [] } = this.props;
      const selectedLocations = locations.filter((location, index) => selectedRows.includes(index));
      const locationIds = selectedLocations.map((location) => location._id);
      return (
        <div>
          <CustomToolbar
            type="Edit"
            onClick={() => this.handleOpenBulkEdit(locationIds)}
          />
          <CustomToolbar
            type="Delete"
            onClick={() => this.handleBulkDeleteLocations(locationIds)}
          />
        </div>
      );
    };

    basicOptions.customToolbar = () => (
      <>
        <CustomToolbar onClick={this.handleUploadLocations} type="Upload" />
        <CustomToolbar onClick={this.handleAddLocation} type="Add" />
      </>
    );

    return basicOptions;
  }

  get columns() {
    const {
      addToColumnData, selectedFilters, primarySort,
      locations,
    } = this.props;

    // the order of objects in columnData needs to match
    // the order of objects in data - both are referenced
    // on the render of the MUI datatable
    // columnData[0] is hidden from view, but contains the locationId
    // and is used to allow the onRowClick above to know what location
    // it is referencing
    const columnData = [
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: NAME,
        options: {
          customBodyRender: (data) => {
            if (data) {
              let displayName = data;
              if (data.length > 16) {
                const shortenedName = data.slice(0, 16);
                displayName = `${shortenedName} ...`;
              }
              return (
                <Tooltip title={data}>
                  <span style={styles.noWrap}>{displayName}</span>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${NAME}: ${v}`,
          },
        },
      },
      {
        name: TYPE,
        options: {
          customFilterListOptions: {
            render: (v) => `${TYPE}: ${v}`,
          },
        },
      },
      {
        name: STATUS,
        options: {
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: ACTUAL_VEHICLES,
        options: {
          customFilterListOptions: {
            render: (v) => `${ACTUAL_VEHICLES}: ${v}`,
          },
        },
      },
      {
        name: TARGET,
        options: {
          customFilterListOptions: {
            render: (v) => `${TARGET}: ${v}`,
          },
        },
      },
      {
        name: CAPACITY,
        options: {
          customFilterListOptions: {
            render: (v) => `${CAPACITY}: ${v}`,
          },
        },
      },
      {
        name: ACTIONS,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (locId) => {
            const matchingLoc = find(locations, (loc) => loc._id === locId);
            if (locId) {
              return (
                <Tooltip title={DELETE_LOC}>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      this.handleDeleteLocation(matchingLoc);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'Latitude',
        options: {
          searchable: false,
          display: 'excluded',
          filter: false,
        },
      },
      {
        name: 'Longitude',
        options: {
          searchable: false,
          display: 'excluded',
          filter: false,
        },
      },
    ];

    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const data = this.locations.map((location) => {
      const tableData = [
        get(location, '_id', ''),
        get(location, 'name', ''),
        get(location, 'type', ''),
        get(location, 'status', ''),
        get(location, 'vehicleIds', []).length,
        get(location, 'targetVehicleCounts.link', ''),
        get(location, 'hubCapacity', ''),
        get(location, '_id', ''),
        get(location, 'geo.coordinates[1]', ''),
        get(location, 'geo.coordinates[0]', ''),
      ];
      return tableData;
    });

    return data;
  }

  handleUploadLocations() {
    this.setState({
      isBulkUploadFormOpen: true,
    });
  }

  handleCloseBulkUploadForm() {
    this.setState({ isBulkUploadFormOpen: false });
  }

  handleSaveBulkUploadForm(locations, overwrite) {
    const {
      bulkUploadLocations,
      showSuccessSnackbar,
      showErrorSnackbar,
      fetchLocations,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');

    bulkUploadLocations(marketId, locations, overwrite)
      .then((data) => {
        const errors = get(data, 'data.errors', []);
        const successes = get(data, 'data.successes', []);
        const statusCode = get(data, 'status');
        const errorLocNames = errors.map((err) => err.hubName);
        if (statusCode !== 200 && statusCode !== 201) {
          showErrorSnackbar('Server error on upload');
        } else if (errors && errors.length > 0) {
          showErrorSnackbar(`Error creating: ${errorLocNames}`);
        } else if (successes && successes.length) {
          showSuccessSnackbar(`Successfully uploaded ${successes.length} locations`);
        }
        const stateUpdate = {
          isBulkUploadFormOpen: false,
        };
        this.setState(stateUpdate);
      })
      .then(() => fetchLocations({ market_id: marketId }))
      .catch((error) => {
        showErrorSnackbar(`Error from server: ${error}`);
      });
  }

  handleOpenBulkEdit(selectedRows) {
    this.setState({ isBulkEditFormOpen: true, locationIds: selectedRows });
  }

  handleCloseBulkEdit() {
    this.setState({ isBulkEditFormOpen: false });
  }

  handleSaveBulkEdit(locationIds, newCapacity, newStatus) {
    const {
      bulkEditLocations,
      showSuccessSnackbar,
      showErrorSnackbar,
      fetchLocations,
      clearSelectedRows,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');
    let query;

    if (marketId) {
      query = { market_id: marketId };
    }
    bulkEditLocations(locationIds, newCapacity, newStatus)
      .then(() => {
        clearSelectedRows();
        const stateUpdate = {
          isBulkEditFormOpen: false,
        };
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Successfully updated ${locationIds.length} locations.`);
        });
      })
      .then(() => fetchLocations(query))
      .catch((error) => {
        const errMsg = get(error, 'response.data.message', '');
        showErrorSnackbar(`Error from server: ${errMsg}`);
      });
  }

  handleBulkDeleteLocations(locationIds) {
    this.setState({ locationIds, warnBeforeDeleting: true, bulkDeleting: true });
  }

  handleAddLocation() {
    const { marketId } = get(this, 'props.match.params');
    const selectedMarketGeo = get(this, 'props.selectedMarket.geo');

    const newLocation = {
      _id: nanoid(24),
      status: OPEN,
      marketId,
      geo: selectedMarketGeo,
    };

    this.setState({
      editLocation: newLocation,
      isLocationFormOpen: true,
      editing: false,
    });
  }

  handleCloseForm() {
    this.setState({ isLocationFormOpen: false, editing: false, editLocation: {} });
  }

  handleSaveForm(location, editing) {
    const {
      fetchZones,
      fetchLocations,
      createLocation,
      updateLocation,
      match,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;

    let query;
    const marketId = get(match, 'params.marketId');
    if (marketId) {
      query = { market_id: marketId };
    }
    const updateMethod = editing ? updateLocation : createLocation;
    updateMethod(location, location._id)
      .then((data) => {
        const locationName = get(data, 'data.name');

        const stateUpdate = {
          isLocationFormOpen: false,
          editing: false,
          editLocation: {},
        };
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Success ${editing ? 'updating' : 'creating'} location: ${locationName}`);
        });
      })
      .then(() => fetchZones(query))
      .then(() => fetchLocations(query))
      .catch((error) => {
        showErrorSnackbar(`Error from server: ${error}`);
      });
  }

  handleViewLocation(locId) {
    const { locations } = this.props;

    const editLocation = matchingLocation(locations, locId);

    this.setState({
      editLocation,
      isLocationFormOpen: true,
      editing: true,
    });
  }

  handleDeleteLocation(location) {
    this.setState({ editLocation: location, warnBeforeDeleting: true });
  }

  render() {
    const {
      getTitleFromData, isLoading,
      showSuccessSnackbar, showErrorSnackbar,
      fetchZones, fetchLocations,
      bulkEditLocations, clearSelectedRows,
    } = this.props;
    const {
      editLocation,
      editing,
      isLocationFormOpen,
      isBulkUploadFormOpen,
      locationIds,
      isBulkEditFormOpen,
      warnBeforeDeleting,
      bulkDeleting,
    } = this.state;
    const displayData = this.data;
    const { marketId } = get(this, 'props.match.params');
    const locationCount = locationIds.length;
    const DELETE_LOCS_WARN = `Are you sure you want to delete ${locationCount} Location(s)?`;
    const DELETE_LOCS_CONFIRM = `Successfully deleted ${locationCount} Locations(s)`;
    const DELETE_LOCS_ERROR = `Error deleting ${locationCount} Location(s)`;
    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3 }}>
          <MUIDataTable
            title={getTitleFromData('Locations', displayData)}
            columns={this.columns}
            data={displayData}
            options={this.tableOptions}
          />
          <LocationEditForm
            key={`location-edit-form-${editLocation._id}`}
            isOpen={isLocationFormOpen}
            onCancel={this.handleCloseForm}
            onSave={this.handleSaveForm}
            editLocation={editLocation}
            editing={editing}
          />
          {isBulkUploadFormOpen && (
            <BulkUploadModal
              isOpen={isBulkUploadFormOpen}
              onCancel={this.handleCloseBulkUploadForm}
              onSave={this.handleSaveBulkUploadForm}
              type="hubs"
            />
          )}
          {isBulkEditFormOpen && locationIds && (
            <BulkEditLocations
              open={isBulkEditFormOpen}
              handleSave={this.handleSaveBulkEdit}
              handleClose={this.handleCloseBulkEdit}
              locationIds={locationIds}
            />
          )}

          {editLocation && warnBeforeDeleting && (
            <WarnBeforeDeleting
              isOpen={warnBeforeDeleting}
              message={DELETE_WARN}
              onCancel={() => {
                this.setState({ warnBeforeDeleting: false });
              }}
              onConfirm={() => {
                bulkEditLocations([editLocation._id], '', DELETED)
                  .then(() => {
                    showSuccessSnackbar(DELETE_CONFIRM);
                    this.setState({ warnBeforeDeleting: false });
                    clearSelectedRows();
                    fetchZones({ market_id: marketId });
                    fetchLocations({ market_id: marketId });
                  })
                  .catch((error) => {
                    showErrorSnackbar(`${DELETE_ERROR}: ${error}`);
                  });
              }}
            />
          )}

          {locationIds && locationCount > 0 && bulkDeleting && warnBeforeDeleting && (
            <WarnBeforeDeleting
              isOpen={warnBeforeDeleting}
              message={DELETE_LOCS_WARN}
              onCancel={() => {
                this.setState({ warnBeforeDeleting: false, bulkDeleting: false });
              }}
              onConfirm={() => {
                bulkEditLocations(locationIds, '', DELETED)
                  .then(() => {
                    showSuccessSnackbar(DELETE_LOCS_CONFIRM);
                    this.setState({
                      locationIds: [],
                      warnBeforeDeleting: false,
                      bulkDeleting: false,
                    });
                    clearSelectedRows();
                    fetchZones({ market_id: marketId });
                    fetchLocations({ market_id: marketId });
                  })
                  .catch((error) => {
                    showErrorSnackbar(`${DELETE_LOCS_ERROR}: ${error}`);
                  });
              }}
            />
          )}
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapStateToProps = ({ markets, locations, vehicles }) => ({
  isLoading: locations.isLoading || markets.isLoading || vehicles.isLoading,
  selectedMarket: markets.selectedMarket,
  marketVehicles: vehicles.marketVehicles,
  locations: locations.marketLocations,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...locationActions,
  ...marketActions,
  ...vehicleActions,
  ...zoneActions,
  ...snackbarActions,
}, dispatch);

export default withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(LocationTable)),
  'LocationTable',
);
